import React from 'react';
import { Check, X, Sparkles, Clock, Calendar } from 'lucide-react';
import { GST_RATE } from '../../common/gst';
import { trackButtonClick, trackConversion } from '../../lib/analytics';
import { trackButtonClick as trackFirebaseButtonClick } from '../../firebaseConfig';

interface PricingCardProps {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  duration: string;
  features: { text: string; available: boolean; highlight?: boolean }[];
  highlight: boolean;
  minutes: number;
  isUnlimited: boolean;
  isActive?: boolean;
  expiryDate?: string;
  isDowngrade?: boolean;
  isUpgrade?: boolean;
  isProcessing?: boolean;
  hasCouponApplied?: boolean;
  hasDiscount?: boolean;
  discountPercentage?: number;
  couponValidButNotApplicable?: boolean;
  couponMinPurchaseNotMet?: boolean;
  freeMinutes?: number;
  shouldShowPricing?: boolean;
  userLocation?: { country: string; countryCode: string } | null;
  onChoosePlan?: () => void;
}

export function PricingCard({
  id,
  name,
  price,
  originalPrice,
  duration,
  features,
  highlight,
  minutes,
  isUnlimited,
  isActive = false,
  expiryDate,
  isDowngrade = false,
  isUpgrade = false,
  isProcessing = false,
  hasCouponApplied = false,
  hasDiscount = false,
  discountPercentage = 0,
  couponValidButNotApplicable = false,
  couponMinPurchaseNotMet = false,
  freeMinutes,
  shouldShowPricing = true,
  userLocation,
  onChoosePlan
}: PricingCardProps) {

  const handleChoosePlan = () => {
    if (isActive || isDowngrade || isProcessing) return;

    // Track button click with unified analytics (Firebase, Meta Pixel, GA4, Clarity)
    const buttonName = shouldShowPricing ? 'Choose Plan' : 'Signup & Request Plan Trial';
    trackButtonClick(buttonName, 'Pricing Card', {
      plan_id: id,
      plan_name: name,
      plan_price: price,
      plan_duration: duration,
      plan_minutes: minutes,
      is_highlighted: highlight,
      has_discount: hasDiscount,
      discount_percentage: discountPercentage,
      has_coupon: hasCouponApplied,
      is_upgrade: isUpgrade,
      user_country: userLocation?.country,
      should_show_pricing: shouldShowPricing,
      button_type: shouldShowPricing ? 'plan_selection' : 'trial_request'
    });

    // Track conversion event with unified analytics
    if (shouldShowPricing) {
      trackConversion('lead_generation_indian_signup', price, 'INR');
    } else {
      trackConversion('trial_request_foreign', price, 'INR');
    }

    // Keep Firebase analytics for backward compatibility
    trackFirebaseButtonClick(buttonName, 'Pricing Card', {
      plan_id: id,
      plan_name: name,
      plan_price: price,
      plan_duration: duration,
      plan_minutes: minutes,
      is_highlighted: highlight,
      has_discount: hasDiscount,
      discount_percentage: discountPercentage,
      has_coupon: hasCouponApplied,
      is_upgrade: isUpgrade,
      user_country: userLocation?.country,
      should_show_pricing: shouldShowPricing,
      button_type: shouldShowPricing ? 'plan_selection' : 'trial_request'
    });

    // Call the original onChoosePlan handler
    if (onChoosePlan) {
      onChoosePlan();
    }
  };

  return (
    <div className={`
      group relative rounded-3xl p-px isolate overflow-hidden
      ${highlight ? 'bg-gradient-to-br from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark' : 'bg-gradient-to-br from-border-light via-primary-light/20 to-border-light dark:from-border-dark dark:via-primary-dark/20 dark:to-border-dark'}
      
    `}>
      {/* Animated gradient border */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 animate-gradient bg-[length:200%_auto]" />
      
      {/* Glow effect */}
      <div className={`
        absolute -inset-px rounded-3xl blur-xl
        ${highlight ? 'bg-gradient-to-br from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-40' : 'opacity-0'}
        transition-opacity duration-300 group-hover:opacity-40
      `} />

      {/* Coupon badge */}
      {hasCouponApplied && hasDiscount && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold font-heading">
            {discountPercentage}% Off
          </div>
        </div>
      )}

      {/* Coupon badge */}
      {highlight && (
        <div className="absolute -top-0 -left-0 z-10">
          <div className="bg-blue-600 text-white px-4 py-1 rounded-br-lg rounded-tl-xl text-sm font-semibold font-heading">
           Popular
          </div>
        </div>
      )}
      {/* Free minutes badge */}
      {freeMinutes !== undefined && freeMinutes > 0 && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-green-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold shadow-md font-heading">
            Free additional {freeMinutes} minutes
          </div>
        </div>
      )}

      {/* Glass container - Consistent height for all cards */}
      <div className="relative h-full min-h-[600px] flex flex-col rounded-[1.4rem] bg-gradient-to-br from-background-light/90 to-surface-light/90 dark:from-background-dark/90 dark:to-surface-dark/90 backdrop-blur-xl">
        {/* Content with consistent padding */}
        <div className="px-8 pt-4 pb-4 flex-1 mt-4">
          {/* Header */}
          <div className="text-center mb-8 mt-4">
            <h3 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark">
              {name}
            </h3>
            <div className="flex items-baseline justify-center gap-2 mb-4">
              {shouldShowPricing ? (
                <>
                  {originalPrice && (
                    <span className="text-lg text-text-secondary-light dark:text-text-secondary-dark line-through opacity-75">
                      ₹{originalPrice.toFixed(0)}
                    </span>
                  )}
                  <span className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark">
                    ₹{price.toFixed(0)}
                  </span>
                  <span className={`text-text-secondary-light dark:text-text-secondary-dark ${duration.length > 6 ? 'text-base md:text-lg' : 'text-lg'}`}>
                    /{duration}
                  </span>
                </>
              ) : (
                <div className="text-center">
                  <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark">
                    Request Plan Trial
                  </span>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2 max-w-md mx-auto">
                    We don't support payments from <span className="text-text-primary-light dark:text-text-primary-dark">{userLocation?.country}</span> yet. But you can request a trial plan.
                  </p>
                </div>
              )}
            </div>
            {/* GST Text Display */}
            {price > 0 && shouldShowPricing && (
              <div className="text-center mb-4">
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark font-heading font-medium">
                <span className="text-text-primary-light dark:text-text-primary-dark">+{GST_RATE}% GST</span> on the amount
                </p>
              </div>
            )}
            <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
              <Clock size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
              <span className="text-base font-medium text-primary-light dark:text-primary-dark">
                {isUnlimited ? (<>
                <div className="flex flex-row items-center gap-1 h-[24px]">
                      <span className="text-2xl">∞ </span> <span className="">Minutes</span>
                      </div>
                      </>) : `${minutes} Minutes`}
              </span>
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-4 mb-8">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className={`mt-0.5 p-1 rounded-full ${feature.available 
                  ? 'bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20' 
                  : 'bg-red-500/10'}`}
                >
                  {feature.available 
                    ? <Check className="w-3.5 h-3.5 text-primary-light dark:text-primary-dark" />
                    : <X className="w-3.5 h-3.5 text-red-500" />
                  }
                </span>
                <span className={`${feature.available 
                  ? feature.highlight 
                    ? 'text-base text-green-700 dark:text-green-300 font-medium' 
                    : 'text-base text-text-secondary-light dark:text-text-secondary-dark'
                  : 'text-base text-text-tertiary-light dark:text-text-tertiary-dark line-through opacity-75'}`}
                >
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Button and expiry date in a fixed position at the bottom with consistent padding */}
        <div className="px-5 md:px-6 pb-8 mt-auto">
          {/* CTA Button */}
          <button 
            onClick={handleChoosePlan}
            disabled={isActive || isDowngrade || isProcessing}
            className={`
              w-full text-lg py-3 group/btn relative overflow-hidden
              ${isActive ? 'cursor-not-allowed opacity-75 bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark' : ''}
              ${isDowngrade ? 'opacity-50 cursor-not-allowed' : ''}
              ${isActive ? 'btn-secondary' : highlight ? 'btn-primary' : 'btn-secondary'}
            `}
          >
                <span className="relative z-10 inline-flex items-center gap-2">
              {isActive ? 'Current Plan' : 
              isDowngrade ? 'Cannot Downgrade' : 
              isProcessing ? 'Processing...' : 
              shouldShowPricing ? 'Choose Plan' : 'Signup & Request Plan Trial'}
              {!isActive && !isDowngrade && !isProcessing && (
                <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover/btn:scale-110" />
              )}
            </span>
          </button>
          
          {/* Show expiry date below CTA button if this is the active plan */}
          {isActive && expiryDate && (
            <div className="mt-4 flex items-center justify-center text-amber-500 text-sm">
              <Calendar size={14} className="mr-1" />
              <span>Expires on {expiryDate}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}